import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
} from '@chakra-ui/react';
import { useModalProps } from 'app/components/Settings/useModalProps';
import { ChakraAutocompleteField, InputField } from 'app/components/Core';
import { Option } from 'types/general.types';
import { useFetchValidationSchema, useResource } from 'app/utils/hooks';
import { fetchRepresentativeRolesEffect } from 'app/store/Profile/effects';
import {
  getRepresentativeRoles,
  getUpdateLink,
} from 'app/store/Profile/selectors';
import { Representative } from 'types/profile';
import { useAppSelector } from 'app/store';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { dynamicYupResolver } from 'app/utils/utils';
import { RepresentativeFormData } from '../../ProfileForm/types';

export interface RepresentativeDialogComponentProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: RepresentativeFormData) => Promise<void>;
  index?: number;
  represantative?: Representative | null;
  isEditing: boolean;
}

export const RepresentativeDialogComponent: React.FC<RepresentativeDialogComponentProps> = ({
  open,
  onClose,
  onSubmit,
  represantative,
  isEditing,
}) => {
  const defaultValues: RepresentativeFormData = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      publisherIpi: '',
      role: undefined,
    }),
    []
  );

  const link = useAppSelector(getUpdateLink);
  const { validationSchema } = useFetchValidationSchema({ link });

  const yupValidationSchema = transformSchemaToYup<RepresentativeFormData>(
    validationSchema?.properties?.representatives?.items,
    defaultValues
  );

  const form = useForm<RepresentativeFormData>({
    mode: 'all',
    defaultValues,
    context: {
      yupValidationSchema,
    },
    resolver: dynamicYupResolver,
  });

  const {
    handleSubmit,
    register,
    reset,
    resetField,
    control,
    formState: { isSubmitting, isValid, errors, isDirty },
  } = form;

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const { modalProps, contentProps } = useModalProps(open);

  const { dataTestId, title, buttonLabel } = isEditing
    ? {
        dataTestId: 'edit-representative-form',
        title: 'Edit Representative',
        buttonLabel: 'Save',
      }
    : {
        dataTestId: 'add-representative-form',
        title: 'Add Representative',
        buttonLabel: 'Add',
      };

  const [representativeRoles] = useResource(
    fetchRepresentativeRolesEffect,
    getRepresentativeRoles
  );

  const roleOptions: Option[] = useMemo(
    () =>
      !representativeRoles
        ? []
        : Object.values(representativeRoles).map(({ key, name }) => ({
            title: name,
            value: key,
          })),
    [representativeRoles]
  );

  useEffect(() => {
    if (open && represantative) {
      form.reset({
        ...defaultValues,
        ...represantative,
        publisherIpi: represantative.publisherIpi?.toString() || '',
        role: roleOptions?.find(({ value }) => value === represantative.role),
      });
    }
  }, [represantative, form, open, roleOptions, defaultValues]);

  return (
    <Modal isOpen={open} onClose={onClose} size="md" {...modalProps}>
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} data-testid={dataTestId}>
            <ModalHeader data-testid="dialog-title" title={title}>
              {title}
            </ModalHeader>
            <ModalBody overscrollBehavior="none">
              <SimpleGrid columns={2} spacingX={4}>
                <InputField
                  {...register('firstName')}
                  label="FirstName"
                  isInvalid={Boolean(errors.firstName)}
                  errorMessage={errors.firstName?.message}
                  data-testid="FirstName"
                />
                <InputField
                  {...register('lastName')}
                  label="LastName"
                  isInvalid={Boolean(errors.lastName)}
                  errorMessage={errors.lastName?.message}
                  data-testid="LastName"
                />
                <InputField
                  {...register('email')}
                  label="Email"
                  isInvalid={Boolean(errors.email)}
                  errorMessage={errors.email?.message}
                  data-testid="Email"
                />
                <ChakraAutocompleteField
                  dataTestId="Role"
                  name="role"
                  label="Role"
                  control={control}
                  isInvalid={Boolean(errors?.role)}
                  errorMessage={errors?.role?.message}
                  options={roleOptions}
                  isClearable
                  isReadOnly
                  onInputChange={(value) => {
                    if (!value) {
                      resetField('role');
                    }
                  }}
                />
                <InputField
                  {...register('company')}
                  label="Company"
                  isInvalid={Boolean(errors.company)}
                  errorMessage={errors.company?.message}
                  data-testid="Company"
                />
                <InputField
                  {...register('publisherIpi')}
                  label="Publisher IPI"
                  isInvalid={Boolean(errors.publisherIpi)}
                  errorMessage={errors.publisherIpi?.message}
                  data-testid="Publisher IPI"
                />
              </SimpleGrid>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                isDisabled={!isDirty || isSubmitting || !isValid}
                type="submit"
                data-testid="RepresentativeChangeDialog-change-btn"
                ml={2}
              >
                {buttonLabel}
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};
